import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Chip,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  Apps,
  Security,
  Settings,
  AccountCircle,
  Logout,
  ChevronLeft,
  Assignment,
  Assessment,
  Chat as ChatIcon,
  Login as LoginIcon,
  BarChart,
  Policy as PolicyIcon,
  RateReview as RateReviewIcon,
  FactCheck as QuizIcon,
  AccountTree as IGAIcon,
  Psychology as AIIcon,
  Hub as HubIcon,
  VpnKey as VpnKeyIcon,
  Groups as GroupsIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  SettingsBrightness as SystemModeIcon,
  Check as CheckIcon,
  ChevronRight as ChevronRightIcon,
  Contrast as ContrastIcon,
} from '@mui/icons-material';
import { isSuperAdmin } from '../utils/roleAccess';
import { isMercuryTenant } from '../utils/tenantBranding';

import { useAuth } from '../contexts/AuthContext';
import { useTheme as useCustomTheme, AppearanceMode } from '../contexts/ThemeContext';
import ThemeSelector from './ThemeSelector';
import TurboChat from './TurboChat';
import NotificationCenter from './NotificationCenter';
import BrandedWordmark from './BrandedWordmark';
import TenantLogo from './TenantLogo';
import {
  ensureTenantBrandingDefaults,
  readBrandingFromStorage,
  resolveBrandingFromParsed,
  ResolvedBranding
} from '../utils/brandingStorage';
import { authMethodLabel, getStoredAuthMethod } from '../utils/authMethod';

const APPEARANCE_OPTIONS: { value: AppearanceMode; label: string; icon: React.ReactElement }[] = [
  { value: 'light', label: 'Light', icon: <LightModeIcon fontSize="small" /> },
  { value: 'dark', label: 'Dark', icon: <DarkModeIcon fontSize="small" /> },
  { value: 'system', label: 'System', icon: <SystemModeIcon fontSize="small" /> },
];

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  submenu?: MenuItem[];
}

const COLLAPSED_RAIL = 70;
const LABELED_RAIL = 232;

const NAV_BTN_SX = {
  mx: 0,
  px: 0,
  py: 0.75,
  borderRadius: 2,
  minHeight: 48,
  minWidth: 0,
  width: '100%',
  maxWidth: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'hidden',
} as const;

const NAV_ICON_SX = {
  minWidth: 0,
  width: '100%',
  justifyContent: 'center',
  display: 'flex',
  alignItems: 'center',
  m: 0,
} as const;

const Layout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showNavLabels, setShowNavLabels] = useState(true);
  const railWidth = showNavLabels ? LABELED_RAIL : COLLAPSED_RAIL;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [appearanceAnchorEl, setAppearanceAnchorEl] = useState<null | HTMLElement>(null);
  const [appBarLogoFailed, setAppBarLogoFailed] = useState(false);
  const [branding, setBranding] = useState<ResolvedBranding>(() => ensureTenantBrandingDefaults());
  const { user, logout } = useAuth();
  const { colors, appearance, setAppearance, resolvedMode } = useCustomTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const isDark = resolvedMode === 'dark';

  const signInMethodChip = React.useMemo(() => {
    if (!user) return null;
    return getStoredAuthMethod();
  }, [user, location.pathname]);

  const showSignInMethodChip =
    Boolean(signInMethodChip) &&
    (process.env.NODE_ENV === 'development' || process.env.REACT_APP_SHOW_AUTH_METHOD === 'true');

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
    setAppearanceAnchorEl(null);
  };

  const handleAppearanceMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAppearanceAnchorEl(event.currentTarget);
  };

  const handleAppearanceMenuClose = () => {
    setAppearanceAnchorEl(null);
  };

  const handleAppearanceSelect = (mode: AppearanceMode) => {
    setAppearance(mode);
    setAppearanceAnchorEl(null);
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  React.useEffect(() => {
    try {
      const savedBranding = readBrandingFromStorage();
      setAppBarLogoFailed(false);
      if (savedBranding) {
        const data = JSON.parse(savedBranding) as Record<string, unknown>;
        setBranding(resolveBrandingFromParsed(data));
      } else {
        setBranding(ensureTenantBrandingDefaults());
      }
    } catch (error) {
      console.error('Error loading branding data:', error);
      setBranding(ensureTenantBrandingDefaults());
    }
  }, []);

  // Listen for theme / branding changes
  React.useEffect(() => {
    const reloadBrandingFromStorage = () => {
      try {
        const raw = readBrandingFromStorage();
        if (!raw) return;
        const data = JSON.parse(raw) as Record<string, unknown>;
        setBranding(resolveBrandingFromParsed(data));
        setAppBarLogoFailed(false);
      } catch {
        /* ignore */
      }
    };

    const handleThemeChange = () => {
      setBranding(prev => ({ ...prev }));
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'turboiam-theme' || e.key === 'turboiam-custom-theme') {
        setBranding(prev => ({ ...prev }));
        return;
      }
      if (e.key === 'provysn-branding' || e.key === 'mercury-branding' || e.key === 'turboiam-branding') {
        reloadBrandingFromStorage();
      }
    };

    window.addEventListener('themeChanged', handleThemeChange);
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('brandingChanged', reloadBrandingFromStorage);

    return () => {
      window.removeEventListener('themeChanged', handleThemeChange);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('brandingChanged', reloadBrandingFromStorage);
    };
  }, []);

  const flattenMenuItems = (items: MenuItem[]): MenuItem[] => {
    const flat: MenuItem[] = [];
    for (const item of items) {
      flat.push(item);
      if (item.submenu?.length) flat.push(...item.submenu);
    }
    return flat;
  };

  // Define menu items based on user role
  const getMenuItems = (): MenuItem[] => {
    const baseItems = [
      {
        text: 'Dashboard',
        icon: <Dashboard />,
        path: '/dashboard',
      },
      {
        text: 'VYSOR',
        icon: <ChatIcon />,
        path: '/turbochat',
      },
    ];

    // Super admin: full navigation (every screen/route in the app)
    if (isSuperAdmin(user?.role)) {
      return [
        ...baseItems,
        { text: 'Users', icon: <People />, path: '/users' },
        {
          text: 'Applications',
          icon: <Apps />,
          path: '/applications',
          submenu: [
            { text: 'IGA Onboarding', icon: <IGAIcon sx={{ fontSize: 22 }} />, path: '/applications/iga-onboarding' },
            { text: 'AI Governance', icon: <AIIcon sx={{ fontSize: 22 }} />, path: '/ai-governance' },
          ],
        },
        ...(isMercuryTenant()
          ? []
          : [{ text: 'Dependency Graph', icon: <HubIcon />, path: '/dependency-graph' }]),
        { text: 'SSO Review', icon: <RateReviewIcon />, path: '/sso-review' },
        { text: 'Risk Questions', icon: <QuizIcon />, path: '/risk-questions' },
        { text: 'Risk Policies', icon: <PolicyIcon />, path: '/risk-policies' },
        { text: 'Risk Assessment', icon: <RateReviewIcon />, path: '/risk-assessment-review' },
        { text: 'Reports', icon: <BarChart />, path: '/reports' },
        { text: 'Audit Trail', icon: <Assignment />, path: '/audit-trail' },
        { text: 'Contacts', icon: <GroupsIcon />, path: '/contacts' },
        ...(isMercuryTenant()
          ? []
          : [{ text: 'PAM Dashboard', icon: <VpnKeyIcon />, path: '/pam-dashboard' }]),
        { text: 'Security', icon: <Security />, path: '/security' },
        { text: 'Settings', icon: <Settings />, path: '/settings' },
      ];
    }

    // App admin (primary/secondary owners) can see All Applications with onboarding submenus
    if (user?.role === 'app_admin') {
      return [
        ...baseItems,
        {
          text: 'All Applications',
          icon: <Apps />,
          path: '/applications',
          submenu: [
            {
              text: 'IGA Onboarding',
              icon: <IGAIcon sx={{ fontSize: 22 }} />,
              path: '/applications/iga-onboarding',
            },
            {
              text: 'AI Governance',
              icon: <AIIcon sx={{ fontSize: 22 }} />,
              path: '/ai-governance',
            },
          ],
        },
        {
          text: 'Reports',
          icon: <BarChart />,
          path: '/reports',
        },
        {
          text: 'Audit Trail',
          icon: <Assignment />,
          path: '/audit-trail',
        },
        {
          text: 'Contacts',
          icon: <GroupsIcon />,
          path: '/contacts',
        },
      ];
    }

    // IGA Admin can see IGA-specific menu items
    if (user?.role === 'iga_admin') {
      return [
        ...baseItems,
        {
          text: 'Applications',
          icon: <Apps />,
          path: '/applications',
          submenu: [
            {
              text: 'IGA Onboarding',
              icon: <IGAIcon sx={{ fontSize: 22 }} />,
              path: '/applications/iga-onboarding',
            },
          ],
        },
        {
          text: 'AI Governance',
          icon: <AIIcon sx={{ fontSize: 22 }} />,
          path: '/ai-governance',
        },
        {
          text: 'Reports',
          icon: <BarChart />,
          path: '/reports',
        },
        {
          text: 'Audit Trail',
          icon: <Assignment />,
          path: '/audit-trail',
        },
        {
          text: 'Contacts',
          icon: <GroupsIcon />,
          path: '/contacts',
        },
        {
          text: 'Settings',
          icon: <Settings />,
          path: '/settings',
        },
      ];
    }

    // SSO Admin can see SSO-specific menu items
    if (user?.role === 'sso_admin') {
      return [
        ...baseItems,
        {
          text: 'Users',
          icon: <People />,
          path: '/users',
        },
        {
          text: 'Applications',
          icon: <Apps />,
          path: '/applications',
        },
        {
          text: 'SSO Review',
          icon: <RateReviewIcon />,
          path: '/sso-review',
        },
        {
          text: 'Reports',
          icon: <BarChart />,
          path: '/reports',
        },
        {
          text: 'Audit Trail',
          icon: <Assignment />,
          path: '/audit-trail',
        },
        {
          text: 'Contacts',
          icon: <GroupsIcon />,
          path: '/contacts',
        },
        {
          text: 'Settings',
          icon: <Settings />,
          path: '/settings',
        },
      ];
    }

    // PAM Admin can see PAM-specific menu items
    if (user?.role === 'pam_admin') {
      return [
        ...baseItems,
        {
          text: 'Applications',
          icon: <Apps />,
          path: '/applications',
        },
        {
          text: 'Reports',
          icon: <BarChart />,
          path: '/reports',
        },
        {
          text: 'Audit Trail',
          icon: <Assignment />,
          path: '/audit-trail',
        },
        {
          text: 'Contacts',
          icon: <GroupsIcon />,
          path: '/contacts',
        },
        {
          text: 'Settings',
          icon: <Settings />,
          path: '/settings',
        },
      ];
    }

    // GRC Admin manages risk questionnaires, controls, and approves assessments
    if (user?.role === 'grc_admin') {
      return [
        ...baseItems,
        {
          text: 'Applications',
          icon: <Apps />,
          path: '/applications',
        },
        {
          text: 'AI Governance',
          icon: <AIIcon sx={{ fontSize: 22 }} />,
          path: '/ai-governance',
        },
        {
          text: 'Risk Questions',
          icon: <QuizIcon />,
          path: '/risk-questions',
        },
        {
          text: 'Risk Policies',
          icon: <PolicyIcon />,
          path: '/risk-policies',
        },
        {
          text: 'Risk Assessment',
          icon: <RateReviewIcon />,
          path: '/risk-assessment-review',
        },
        {
          text: 'Contacts',
          icon: <GroupsIcon />,
          path: '/contacts',
        },
      ];
    }

    // Auditor can see audit trail
    if (user?.role === 'auditor') {
      return [
        ...baseItems,
        {
          text: 'Reports',
          icon: <BarChart />,
          path: '/reports',
        },
        {
          text: 'Audit Trail',
          icon: <Assignment />,
          path: '/audit-trail',
        },
        {
          text: 'Applications',
          icon: <Apps />,
          path: '/applications',
        },
        {
          text: 'Contacts',
          icon: <GroupsIcon />,
          path: '/contacts',
        },
      ];
    }

    // Delegates can see All Applications with onboarding submenus for delegated apps/instances
    if (user?.role === 'delegate') {
      return [
        ...baseItems,
        {
          text: 'All Applications',
          icon: <Apps />,
          path: '/applications',
          submenu: [
            {
              text: 'IGA Onboarding',
              icon: <IGAIcon sx={{ fontSize: 22 }} />,
              path: '/applications/iga-onboarding',
            },
            {
              text: 'AI Governance',
              icon: <AIIcon sx={{ fontSize: 22 }} />,
              path: '/ai-governance',
            },
          ],
        },
        {
          text: 'Reports',
          icon: <Assessment />,
          path: '/reports',
        },
        {
          text: 'Contacts',
          icon: <GroupsIcon />,
          path: '/contacts',
        },
      ];
    }

    // Regular users can see All Applications with onboarding submenus
    if (user?.role === 'user') {
      return [
        ...baseItems,
        {
          text: 'All Applications',
          icon: <Apps />,
          path: '/applications',
          submenu: [
            {
              text: 'IGA Onboarding',
              icon: <IGAIcon sx={{ fontSize: 22 }} />,
              path: '/applications/iga-onboarding',
            },
            {
              text: 'AI Governance',
              icon: <AIIcon sx={{ fontSize: 22 }} />,
              path: '/ai-governance',
            },
          ],
        },
        {
          text: 'Contacts',
          icon: <People />,
          path: '/contacts',
        },
        {
          text: 'Reports',
          icon: <Assessment />,
          path: '/reports',
        },
      ];
    }

    // Fallback for any other roles
    return [
      ...baseItems,
      {
        text: 'Applications',
        icon: <Apps />,
        path: '/applications',
      },
      {
        text: 'Reports',
        icon: <Assessment />,
        path: '/reports',
      },
    ];
  };

  // Update page title when branding or route changes
  React.useEffect(() => {
    const matched = flattenMenuItems(getMenuItems()).find((item) => item.path === location.pathname)?.text;
    const currentPage = location.pathname.startsWith('/user-profile/')
      ? 'Profile'
      : matched || (location.pathname === '/' || location.pathname === '/dashboard' ? 'Dashboard' : 'Page not available');
    document.title = `${currentPage} - ${branding.companyName}`;
  }, [branding.companyName, location.pathname, user?.role]);

  const renderNavButton = (item: MenuItem, selected: boolean) => (
    <ListItem disablePadding sx={{ width: '100%' }}>
      <Tooltip title={item.text} placement="right" arrow>
        <Box component="span" sx={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
          <ListItemButton
            selected={selected}
            aria-label={item.text}
            onClick={() => navigate(item.path)}
            sx={{
              ...NAV_BTN_SX,
              justifyContent: showNavLabels ? 'flex-start' : 'center',
              px: showNavLabels ? 1.5 : 0,
              '&.Mui-selected': {
                backgroundColor: isDark ? `${colors.primary}33` : colors.light,
                color: colors.primary,
                '&:hover': {
                  backgroundColor: isDark ? `${colors.primary}44` : colors.light,
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                ...NAV_ICON_SX,
                width: showNavLabels ? 36 : '100%',
                minWidth: showNavLabels ? 36 : 0,
                color: selected ? colors.primary : 'text.secondary',
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {React.cloneElement(item.icon, { sx: { fontSize: 22, display: 'block' } })}
              </Box>
            </ListItemIcon>
            {showNavLabels && (
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: selected ? 700 : 500, noWrap: true }}
              />
            )}
          </ListItemButton>
        </Box>
      </Tooltip>
    </ListItem>
  );

  const drawer = (
    <Box
      sx={{
        width: railWidth,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
      }}
    >
      <Toolbar sx={{ minHeight: '64px !important', px: 0, flexShrink: 0 }} />
      <List
        sx={{
          flex: 1,
          minHeight: 0,
          pt: 0.5,
          pb: 2,
          px: 0,
          width: railWidth,
          overflowX: 'hidden',
          overflowY: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {getMenuItems().flatMap((item) => {
          const rows: MenuItem[] = item.submenu ? [item, ...item.submenu] : [item];
          return rows.map((row) => (
            <React.Fragment key={`${item.text}-${row.path}`}>
              {renderNavButton(row, location.pathname === row.path)}
            </React.Fragment>
          ));
        })}
      </List>
      <Box sx={{ px: showNavLabels ? 1.5 : 0.5, pb: 1.5, flexShrink: 0 }}>
        <ListItemButton
          aria-label={showNavLabels ? 'Hide navigation labels' : 'Show navigation labels'}
          onClick={() => setShowNavLabels((open) => !open)}
          sx={{ borderRadius: 2, justifyContent: showNavLabels ? 'flex-start' : 'center', px: showNavLabels ? 1.5 : 0 }}
        >
          <ListItemIcon sx={{ minWidth: showNavLabels ? 36 : 0, justifyContent: 'center' }}>
            {showNavLabels ? <ChevronLeft /> : <ChevronRightIcon />}
          </ListItemIcon>
          {showNavLabels && (
            <ListItemText primary="Hide labels" primaryTypographyProps={{ fontSize: '0.82rem' }} />
          )}
        </ListItemButton>
      </Box>
    </Box>
  );

  const mainContent = (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        p: 3,
        backgroundColor: 'background.default',
        minHeight: '100vh',
        marginTop: '64px', // Account for fixed AppBar height
        width: { md: `calc(100% - ${railWidth}px)` },
        marginLeft: { md: 0 },
        overflowX: 'hidden',
      }}
    >
      <Outlet />
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        '--provysn-rail': '0px',
        [muiTheme.breakpoints.up('md')]: {
          '--provysn-rail': `${railWidth}px`,
        },
      }}
    >
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: colors.primary,
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: '64px !important',
            px: { xs: 2, sm: 3 },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <IconButton
              color="inherit"
              aria-label="Open navigation"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
              <TenantLogo
                branding={branding}
                variant="appBar"
                failed={appBarLogoFailed}
                onError={() => setAppBarLogoFailed(true)}
                wordmarkTone="onPrimary"
                wordmarkSx={{
                  fontWeight: 600,
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  minWidth: 0,
                  color: 'inherit'
                }}
              />
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* VYSOR (chat widget) */}
            <Box>
              <TurboChat />
            </Box>
            
            {/* Notification Center */}
            <Box>
              <NotificationCenter />
            </Box>
            
            {/* Theme Selector */}
            <ThemeSelector />

            {showSignInMethodChip && signInMethodChip && (
              <Tooltip title="How this session was signed in (dev / REACT_APP_SHOW_AUTH_METHOD)">
                <Chip
                  label={authMethodLabel(signInMethodChip)}
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'inherit',
                    border: '1px solid rgba(255,255,255,0.35)',
                  }}
                />
              </Tooltip>
            )}

            {/* User Menu */}
            <Box
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', ml: 1 }}
              onClick={handleProfileMenuOpen}
            >
              <IconButton color="inherit" edge="end" size="small" aria-label="Account menu">
                <AccountCircle />
              </IconButton>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: 'inherit', ml: 0.5, display: { xs: 'none', sm: 'block' } }}>
                {user ? `${user.first_name} ${user.last_name}`.trim() || user.email : ''}
              </Typography>
            </Box>
          </Box>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            PaperProps={{ sx: { minWidth: 220 } }}
          >
            <MenuItem onClick={() => { handleProfileMenuClose(); navigate(`/user-profile/${user?.id}`); }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="My Profile"
                primaryTypographyProps={{ fontSize: '0.82rem' }}
              />
            </MenuItem>
            <MenuItem
              onClick={handleAppearanceMenuOpen}
              selected={Boolean(appearanceAnchorEl)}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <ContrastIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Appearance"
                secondary={APPEARANCE_OPTIONS.find((o) => o.value === appearance)?.label}
                primaryTypographyProps={{ fontSize: '0.82rem' }}
                secondaryTypographyProps={{ fontSize: '0.7rem' }}
              />
              <ChevronRightIcon fontSize="small" sx={{ color: 'text.secondary', ml: 1 }} />
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{ fontSize: '0.82rem' }}
              />
            </MenuItem>
          </Menu>

          <Menu
            anchorEl={appearanceAnchorEl}
            open={Boolean(appearanceAnchorEl)}
            onClose={handleAppearanceMenuClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { minWidth: 180 } }}
          >
            {APPEARANCE_OPTIONS.map((opt) => (
              <MenuItem
                key={opt.value}
                selected={appearance === opt.value}
                onClick={() => handleAppearanceSelect(opt.value)}
                sx={{ py: 0.75 }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>{opt.icon}</ListItemIcon>
                <ListItemText
                  primary={opt.label}
                  primaryTypographyProps={{ fontSize: '0.82rem' }}
                />
                {appearance === opt.value && <CheckIcon fontSize="small" color="primary" />}
              </MenuItem>
            ))}
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Desktop Drawer */}
      <Drawer
        variant="persistent"
        open={!mobileOpen}
        sx={{
          width: railWidth,
          flexShrink: 0,
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            width: railWidth,
            boxSizing: 'border-box',
            overflow: 'hidden',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: railWidth,
            boxSizing: 'border-box',
            overflow: 'hidden',
          },
        }}
      >
        {drawer}
      </Drawer>

      {mainContent}
    </Box>
  );
};

export default Layout;
